<template>
  <div class="close-panel" v-if="hasRates || permissions.advance || subsidyAvailable">
    <div class="close-panel-header">
      <span class="close-panel-title">Закрытие рейса</span>
    </div>

    <div v-if="pendingApproval" class="close-hint close-approval-warn">
      ⏳ Рейс уже ожидает согласования: {{ pendingApproval.text }}. Новое согласование заменит текущее.
    </div>

    <div class="close-grid">
      <div class="close-block block-nds">
        <span class="close-label">НДС</span>
        <div class="nds-toggle">
          <button
            :class="['nds-btn', { active: !withNds }]"
            :disabled="rates.left == null || actionInProgress"
            @click="withNds = false"
          >без НДС</button>
          <button
            :class="['nds-btn', { active: withNds }]"
            :disabled="rates.right == null || actionInProgress"
            @click="withNds = true"
          >с НДС</button>
        </div>
      </div>

      <div class="close-block block-price">
        <span class="close-label">
          Цена
          <span v-if="baseValue != null" class="close-sub">(баз. {{ money(baseValue) }})</span>
        </span>
        <GroupedNumberInput
          aria-label="Цена закрытия, ₽"
          :model-value="priceInput"
          :disabled="priceLocked || actionInProgress"
          :placeholder="effectiveValue != null ? money(effectiveValue) : '—'"
          @update:modelValue="onPriceInput({ target: { value: $event } })"
        />
        <div class="quick-row">
          <button
            v-for="step in leftPriceSteps"
            :key="`p${step}`"
            class="quick-btn"
            :disabled="quickLocked || effectiveValue == null"
            @click="onQuickPrice(step)"
          >{{ step }}</button>
          <button
            class="quick-btn quick-reset"
            title="Сбросить цену"
            :disabled="priceLocked || advanceEnabled"
            @click="resetPrice"
          >✕</button>
          <button
            v-for="step in rightPriceSteps"
            :key="`p${step}`"
            class="quick-btn"
            :disabled="quickLocked || effectiveValue == null"
            @click="onQuickPrice(step)"
          >+{{ step }}</button>
        </div>
      </div>

      <div class="close-block block-term">
        <span class="close-label">Срок оплаты</span>
        <div class="term-dd" ref="termDd">
          <button
            type="button"
            class="term-select"
            :class="{ open: termMenuOpen }"
            :disabled="termLocked || actionInProgress"
            @click="toggleTermMenu"
          >
            <span class="term-select-text">{{ termButtonText }}</span>
            <span class="term-caret">▾</span>
          </button>
          <div v-if="termMenuOpen" class="term-menu">
            <button
              type="button"
              :class="['term-option', { selected: !paymentTerm }]"
              @click="chooseTerm('')"
            >По умолчанию (20-25 БД)</button>
            <button
              v-if="selectedSubsidyRow"
              type="button"
              :class="['term-option', { selected: paymentTerm === selectedSubsidyRow.term_label }]"
              @click="chooseTerm(selectedSubsidyRow.term_label)"
            >{{ prettyTerm(selectedSubsidyRow.term_label) }} (субсидия)</button>
            <button
              v-for="term in paymentTermOptions"
              :key="term"
              type="button"
              :class="['term-option', { selected: paymentTerm === term }]"
              @click="chooseTerm(term)"
            >{{ term }}</button>
          </div>
        </div>
      </div>

      <div class="close-block block-date">
        <span class="close-label">Дата погрузки</span>
        <input
          type="datetime-local"
          :value="dateShown"
          :disabled="dateLocked || actionInProgress"
          @input="adjustedDate = $event.target.value"
        />
        <div class="quick-row">
          <button
            v-for="step in leftDateSteps"
            :key="`d${step}`"
            class="quick-btn"
            :disabled="quickLocked || !hasDateBase"
            @click="onQuickDate(step)"
          >{{ step }}ч</button>
          <button
            class="quick-btn quick-reset"
            title="Сбросить дату"
            :disabled="dateLocked"
            @click="resetDate"
          >✕</button>
          <button
            v-for="step in rightDateSteps"
            :key="`d${step}`"
            class="quick-btn"
            :disabled="quickLocked || !hasDateBase"
            @click="onQuickDate(step)"
          >+{{ step }}ч</button>
        </div>
      </div>
    </div>

    <section v-if="subsidyAvailable" class="subsidy-close">
      <button
        type="button"
        class="subsidy-toggle"
        :class="{ open: subsidyPanelOpen }"
        :aria-expanded="subsidyPanelOpen"
        :disabled="actionInProgress"
        @click="toggleSubsidyPanel"
      >
        <span class="subsidy-toggle-title">🧮 Субсидирование тарифа</span>
        <span class="subsidy-toggle-hint">{{ subsidyPanelOpen ? 'Скрыть' : 'Выбрать срок оплаты' }}</span>
        <span class="subsidy-caret">{{ subsidyPanelOpen ? '▴' : '▾' }}</span>
      </button>
      <div v-if="subsidyPanelOpen" class="subsidy-content">
        <div class="subsidy-table-head">
          <span>Срок</span>
          <span>Скидка</span>
          <span>Цена</span>
          <span>Экономия</span>
        </div>
        <button
          v-for="choice in subsidyChoiceRows"
          :key="choice.row.term_label"
          type="button"
          :class="['subsidy-option', 'subsidy-row', { selected: subsidy.selectedIndex === choice.index }]"
          @click="chooseSubsidy(choice.index)"
        >
          <span class="cell-term">{{ choice.term }}</span>
          <span class="cell-percent">{{ choice.percentText }}</span>
          <span class="cell-price">{{ choice.priceText }}</span>
          <span class="cell-savings">{{ choice.savingsText }}</span>
        </button>
        <div v-if="selectedSubsidyRow" class="subsidy-summary">
          <span>Расчёт от базовой ставки {{ formatMoney(subsidyDisplayBase) }} ₽ {{ withNds ? 'с НДС' : 'без НДС' }}</span>
          <strong>Итог закрытия: {{ subsidyRowParts(selectedSubsidyRow).priceText }} ₽</strong>
        </div>
      </div>
    </section>

    <AdvanceClosePanel v-if="permissions.advance" :order="order" :amount="closePrice" :with-nds="withNds" :can-manage-settings="permissions.advance"
      v-model:enabled="advanceEnabled" v-model:term-id="advanceTermId" :closing-date="adjustedDate"
      :busy="actionInProgress" @state="advanceState = $event" />

    <div class="close-actions">
      <button
        v-if="isAdmin"
        class="operation-btn operation-secondary"
        :disabled="advanceEnabled || subsidyPanelOpen || actionInProgress || Boolean(selectedSubsidyRow)"
        @click="requestApproval"
      >🗳 Согласовать</button>
      <button
        :class="['operation-btn', 'close-main-btn', mainButton.className]"
        :disabled="actionInProgress || plan.error || !plan.operation"
        @click="applyClose"
      >{{ mainButton.label }}</button>
      <button
        class="operation-btn reset-all-btn"
        :disabled="advanceEnabled || actionInProgress"
        title="Сбросить форму"
        @click="resetAll"
      >↻</button>
    </div>
  </div>
</template>

<script>
import { PAYMENT_TERM_OPTIONS, parsePriceInput, prettyPaymentTerm, toApprovePaymentTerm } from '../helpers';
import GroupedNumberInput from '@/components/UI/GroupedNumberInput.vue';
import AdvanceClosePanel from './AdvanceClosePanel.vue';

export default {
  name: 'CloseOrderPanel',
  components: { GroupedNumberInput, AdvanceClosePanel },
  props: {
    order: { type: Object, required: true },
    permissions: { type: Object, default: () => ({}) },
    actionInProgress: { type: Boolean, default: false },
    fetchSubsidyTable: { type: Function, required: true },
  },
  emits: ['close', 'requestApproval'],
  data() {
    return {
      withNds: true,
      priceNoNds: '',
      priceWithNds: '',
      priceTouched: false,
      paymentTerm: '',
      adjustedDate: '',
      subsidy: { loading: false, loaded: false, error: '', rows: [], selectedIndex: null, basePrice: null, basePriceNds: null },
      subsidyShownBase: null,
      subsidyTimer: null,
      subsidyPanelOpen: false,
      termMenuOpen: false,
      paymentTermOptions: PAYMENT_TERM_OPTIONS,
      leftPriceSteps: [-5, -1],
      rightPriceSteps: [1, 5],
      leftDateSteps: [-2, -1],
      rightDateSteps: [1, 2],
      approvalHint: '',
      advanceEnabled: false,
      advanceTermId: null,
      advanceState: { ready: false, types: [] },
    };
  },
  computed: {
    closePrice() { return this.advanceEnabled ? this.advanceBaseValue : (this.parsedPrice ?? this.effectiveValue); },
    isAdmin() {
      return Boolean(this.permissions.admin);
    },
    rates() {
      const approved = this.order.approved_result || {};
      if (approved.confirmed && (approved.new_cost_without_nds != null || approved.new_cost_with_nds != null)) {
        return {
          left: approved.new_cost_without_nds ?? null,
          right: approved.new_cost_with_nds ?? null,
          embeddedLeft: this.order.embedded_route?.cost ?? null,
          embeddedRight: this.order.embedded_route?.cost_nds ?? null,
          hasConfirmedApproved: true,
        };
      }
      const manualLeft = this.order.manual_cost ?? null;
      const manualRight = this.order.manual_cost_nds ?? null;
      const embeddedLeft = this.order.embedded_route?.cost ?? null;
      const embeddedRight = this.order.embedded_route?.cost_nds ?? null;
      const hasManual = manualLeft != null || manualRight != null;
      const hasEmbedded = embeddedLeft != null || embeddedRight != null;
      return {
        left: hasManual ? manualLeft : hasEmbedded ? embeddedLeft : null,
        right: hasManual ? manualRight : hasEmbedded ? embeddedRight : null,
        embeddedLeft,
        embeddedRight,
        hasConfirmedApproved: false,
      };
    },
    pendingApproval() {
      const approved = this.order.approved_result;
      if (!approved || approved.confirmed != null) return null;
      const parts = [];
      if (approved.new_cost_with_nds != null) parts.push(`${this.money(approved.new_cost_with_nds)} с НДС`);
      else if (approved.new_cost_without_nds != null) parts.push(`${this.money(approved.new_cost_without_nds)} без НДС`);
      if (approved.new_pay) parts.push(`срок ${this.prettyTerm(approved.new_pay)}`);
      if (approved.new_date) parts.push(`дата ${String(approved.new_date).slice(0, 16).replace('T', ' ')}`);
      if (!parts.length) parts.push('без параметров');
      return { text: parts.join(' · ') };
    },
    hasRates() {
      return this.rates.left != null || this.rates.right != null;
    },
    effectiveValue() {
      return this.withNds ? this.rates.right : this.rates.left;
    },
    baseValue() {
      return this.withNds ? this.rates.embeddedRight : this.rates.embeddedLeft;
    },
    selectedSubsidyRow() {
      const index = this.subsidy.selectedIndex;
      return index == null ? null : this.subsidy.rows[index] || null;
    },
    subsidyAvailable() {
      return true;
    },
    subsidyChoices() {
      return (this.subsidy.rows || [])
        .map((row, index) => ({ row, index }))
        .filter((choice) => !choice.row.is_base);
    },
    subsidyChoiceRows() {
      return this.subsidyChoices.map((choice) => {
        const parts = this.subsidyRowParts(choice.row);
        return { ...choice, ...parts };
      });
    },
    subsidyDisplayBase() {
      const reported = this.withNds ? this.subsidy.basePriceNds : this.subsidy.basePrice;
      return reported ?? this.subsidyCalcBase;
    },
    advanceBaseValue() {
      return this.subsidyCalcBase ?? this.effectiveValue;
    },
    termButtonText() {
      return this.paymentTerm ? this.prettyTerm(this.paymentTerm) : 'По умолчанию (20-25 БД)';
    },
    rawPriceInput() {
      return this.withNds ? this.priceWithNds : this.priceNoNds;
    },
    subsidyCalcBase() {
      // Считаем субсидию строго от базового тарифа (embedded_route).
      // Старый источник (ручной ввод / эффективная ставка) пока преждевременен —
      // оставлен закомментированным, вернёмся при необходимости.
      // const parsed = parsePriceInput(this.rawPriceInput);
      // return parsed ?? this.effectiveValue;
      return this.baseValue;
    },
    quickLocked() {
      return this.actionInProgress || Boolean(this.selectedSubsidyRow) || this.advanceEnabled;
    },
    priceInput() {
      if (this.advanceEnabled) {
        return this.advanceBaseValue != null ? String(this.advanceBaseValue) : '';
      }
      if (this.selectedSubsidyRow) {
        const price = this.subsidyRowPrice(this.selectedSubsidyRow);
        return price != null ? String(price) : '';
      }
      return this.withNds ? this.priceWithNds : this.priceNoNds;
    },
    priceLocked() {
      return this.actionInProgress || Boolean(this.selectedSubsidyRow) || this.advanceEnabled;
    },
    termLocked() {
      return this.actionInProgress || Boolean(this.selectedSubsidyRow) || this.advanceEnabled;
    },
    dateLocked() {
      return this.actionInProgress || Boolean(this.selectedSubsidyRow) || this.advanceEnabled;
    },
    parsedPrice() {
      return parsePriceInput(this.priceInput);
    },
    dateSourceNaive() {
      const approvedDate = this.order.approved_result?.new_date;
      return this.naiveFromSource(approvedDate || this.order.order?.route?.points?.[0]?.planed_date);
    },
    dateShown() {
      return this.selectedSubsidyRow ? '' : this.adjustedDate;
    },
    hasDateBase() {
      return Boolean(this.adjustedDate || this.dateSourceNaive);
    },
    priceChanged() {
      if (this.selectedSubsidyRow || !this.priceTouched) return false;
      const parsed = this.parsedPrice;
      return parsed != null && this.effectiveValue != null && parsed !== this.effectiveValue;
    },
    priceBelowBase() {
      if (!this.priceChanged || this.baseValue == null) return false;
      return this.parsedPrice < this.baseValue;
    },
    termChanged() {
      return Boolean(this.paymentTerm);
    },
    dateChanged() {
      if (this.selectedSubsidyRow) return false;
      if (!this.adjustedDate) return false;
      if (!this.dateSourceNaive) return true;
      return this.dateSourceNaive !== this.adjustedDate;
    },
    plan() {
      const nds = this.withNds;
      const effective = this.effectiveValue;

      if (this.advanceEnabled) {
        const current = this.advanceState.inputAmount === this.closePrice && this.advanceState.withNds === nds && this.advanceState.termId === this.advanceTermId;
        if (!this.hasRates) return { operation: null, mode: 'advance', error: true, hint: 'Для закрытия установите тариф рейса' };
        if (this.advanceState.refreshing) return { operation: null, mode: 'advance', error: true, hint: '' };
        if (!this.advanceState.ready || !current) return { operation: null, mode: 'advance', error: true, hint: this.advanceState.error || 'Ожидается актуальный расчет авансирования' };
        const payload = { with_nds: nds, advance: this.advanceState.request };
        if (this.dateChanged) payload.adjusted = { new_date: this.naiveToIso(this.adjustedDate) };
        return { operation: 'closeOrder', mode: 'advance', payload, price: this.closePrice,
          hint: `Закрытие с авансированием: ${this.formatMoney(this.closePrice)} · ${this.advanceState.termName}` };
      }

      if (this.selectedSubsidyRow) {
        const row = this.selectedSubsidyRow;
        const price = this.subsidyRowPrice(row);
        return {
          operation: 'closeOrder',
          mode: 'subsidy',
          payload: {
            with_nds: nds,
            payment_term: row.term_label,
            reduction_percent: row.reduction_percent,
            use_subsidy_calc: true,
          },
          price,
          hint: `Субсидирование: ${this.prettyTerm(row.term_label)} · ${row.reduction_percent.toLocaleString('ru-RU')}% · ${price != null ? this.formatMoney(price) : '—'}`,
        };
      }

      if (this.priceChanged && this.parsedPrice <= 0) {
        return { operation: null, error: true, hint: 'Цена должна быть положительной' };
      }

      // Исключение: цена ниже тарифа закрывается напрямую, но только если
      // срок и дата не менялись — иначе менеджеру всё равно нужно согласование
      if (this.priceBelowBase && (this.isAdmin || (!this.termChanged && !this.dateChanged))) {
        return {
          operation: 'closeBelowBase',
          mode: 'belowBase',
          payload: { with_nds: nds, cost: this.parsedPrice },
          price: this.parsedPrice,
          hint: `Ниже базового: ${this.formatMoney(this.parsedPrice)} (баз. ${this.formatMoney(this.baseValue)})`,
        };
      }

      const hasChanges = this.priceChanged || this.termChanged || this.dateChanged;
      if (hasChanges && !this.isAdmin) {
        return {
          operation: 'requestApproval',
          mode: 'approval',
          price: this.priceChanged ? this.parsedPrice : null,
          hint: '',
        };
      }

      if (this.priceChanged || this.dateChanged) {
        return this.adjustedPlan(nds, this.priceChanged ? this.parsedPrice : null);
      }

      if (this.rates.hasConfirmedApproved) {
        return {
          operation: 'closeOrder',
          mode: 'close',
          payload: { with_nds: nds, use_approved_data: true },
          price: effective,
          hint: `По согласованию: ${effective != null ? this.formatMoney(effective) : '—'}`,
        };
      }

      const payload = { with_nds: nds };
      if (this.paymentTerm) payload.payment_term = this.paymentTerm;
      return {
        operation: 'closeOrder',
        mode: 'close',
        payload,
        price: effective,
        hint: this.paymentTerm
          ? `Без изменений цены, срок ${this.prettyTerm(this.paymentTerm)}`
          : 'Без изменений',
      };
    },
    mainButton() {
      const parts = [];
      if (this.plan.price != null) {
        parts.push(`цена ${this.formatMoney(this.plan.price)} ${this.withNds ? 'с НДС' : 'без НДС'}`);
      }
      const term = this.plan.mode === 'advance' ? this.advanceState.termName : (this.selectedSubsidyRow ? this.selectedSubsidyRow.term_label : this.paymentTerm);
      if (term) {
        parts.push(`срок ${this.prettyTerm(term)}`);
      }
      if (this.dateChanged) {
        parts.push(this.adjustedDate.replace('T', ' '));
      }
      if (this.selectedSubsidyRow) {
        parts.push(`скидка ${this.selectedSubsidyRow.reduction_percent.toLocaleString('ru-RU')}%`);
      }
      if (this.plan.mode === 'advance' && this.advanceState.sourceAmount != null) {
        parts.push(`списание ${this.formatMoney(this.advanceState.sourceAmount)}`);
      }
      const suffix = parts.length ? ` (${parts.join(' · ')})` : '';
      if (this.plan.mode === 'advance') return { label: `💳 Закрыть с авансированием${suffix}`, className: 'operation-advance' };
      if (this.plan.mode === 'subsidy') {
        return { label: `🧮 Субсидировать${suffix}`, className: 'operation-subsidy' };
      }
      if (this.plan.mode === 'approval') {
        return { label: `🗳 Согласовать${suffix}`, className: 'operation-approval' };
      }
      if (this.plan.mode === 'belowBase') {
        return { label: `🔥 Закрыть${suffix}`, className: 'operation-primary' };
      }
      return { label: `✅ Закрыть${suffix}`, className: 'operation-primary' };
    },
  },
  watch: {
    advanceEnabled(value) {
      if (value) {
        this.subsidy.selectedIndex = null;
        this.subsidyPanelOpen = false;
      } else {
        this.advanceTermId = null;
        this.advanceState = { ready: false, types: [] };
      }
    },
    'order._id': 'resetPanel',
    withNds() {
      this.priceTouched = false;
      this.scheduleSubsidyPrice();
    },
    rawPriceInput() {
      this.scheduleSubsidyPrice();
    },
  },
  mounted() {
    this.maybeLoadSubsidy();
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    this.clearSubsidyTimer();
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onEsc);
  },
  methods: {
    resetPanel() {
      this.withNds = this.rates.right != null;
      this.priceNoNds = '';
      this.priceWithNds = '';
      this.priceTouched = false;
      this.paymentTerm = '';
      this.adjustedDate = '';
      this.approvalHint = '';
      this.advanceEnabled = false;
      this.advanceTermId = null;
      this.advanceState = { ready: false, types: [] };
      this.clearSubsidyTimer();
      this.subsidyPanelOpen = false;
      this.closeTermMenu();
      this.subsidyShownBase = null;
      this.subsidy = { loading: false, loaded: false, error: '', rows: [], selectedIndex: null, basePrice: null, basePriceNds: null };
      this.maybeLoadSubsidy();
    },
    maybeLoadSubsidy() {
      if (this.subsidyAvailable && !this.subsidy.loading && !this.subsidy.loaded) {
        this.loadSubsidy();
      }
    },
    clearSubsidyTimer() {
      if (this.subsidyTimer) {
        clearTimeout(this.subsidyTimer);
        this.subsidyTimer = null;
      }
    },
    scheduleSubsidyPrice() {
      this.clearSubsidyTimer();
      this.subsidyTimer = setTimeout(() => {
        this.subsidyTimer = null;
        this.subsidyShownBase = this.subsidyCalcBase;
      }, 500);
    },
    onPriceInput(event) {
      this.priceTouched = true;
      this.approvalHint = '';
      if (this.withNds) this.priceWithNds = event.target.value;
      else this.priceNoNds = event.target.value;
    },
    onQuickPrice(step) {
      const base = this.parsedPrice ?? this.effectiveValue;
      if (base == null) return;
      const next = Math.round((base + step) * 10) / 10;
      const value = String(next > 0 ? next : 0);
      this.priceTouched = true;
      this.approvalHint = '';
      if (this.withNds) this.priceWithNds = value;
      else this.priceNoNds = value;
    },
    onQuickDate(hours) {
      const base = this.adjustedDate || this.dateSourceNaive;
      if (!base) return;
      this.adjustedDate = this.shiftNaiveHours(base, hours);
    },
    resetPrice() {
      this.priceNoNds = '';
      this.priceWithNds = '';
      this.priceTouched = false;
      this.approvalHint = '';
    },
    resetDate() {
      this.adjustedDate = '';
    },
    resetAll() {
      this.advanceEnabled = false;
      this.advanceTermId = null;
      this.priceNoNds = '';
      this.priceWithNds = '';
      this.priceTouched = false;
      this.paymentTerm = '';
      this.adjustedDate = '';
      this.approvalHint = '';
      this.subsidyShownBase = this.subsidyCalcBase;
      if (this.subsidy.rows.length) this.subsidy.selectedIndex = null;
      this.closeSubsidyPanel();
      this.closeTermMenu();
    },
    toggleSubsidyPanel() {
      if (this.actionInProgress) return;
      this.subsidyPanelOpen = !this.subsidyPanelOpen;
      if (this.subsidyPanelOpen) {
        this.advanceEnabled = false;
        this.advanceTermId = null;
        this.selectFirstSubsidy();
      } else {
        this.closeSubsidyPanel();
      }
    },
    closeSubsidyPanel() {
      this.subsidyPanelOpen = false;
      this.chooseSubsidy(null);
    },
    onEsc(event) {
      if (event.key !== 'Escape') return;
      this.closeTermMenu();
    },
    onDocClick(event) {
      if (this.termMenuOpen) {
        const root = this.$refs.termDd;
        if (root && !root.contains(event.target)) this.closeTermMenu();
      }
    },
    toggleTermMenu() {
      if (this.termLocked || this.actionInProgress) return;
      this.termMenuOpen = !this.termMenuOpen;
      this.syncEscListener();
    },
    closeTermMenu() {
      this.termMenuOpen = false;
      this.syncEscListener();
    },
    chooseTerm(value) {
      this.paymentTerm = value;
      this.closeTermMenu();
    },
    syncEscListener() {
      const need = this.termMenuOpen;
      if (need) document.addEventListener('keydown', this.onEsc);
      else document.removeEventListener('keydown', this.onEsc);
    },
    chooseSubsidy(index) {
      if (index == null) {
        this.subsidy.selectedIndex = null;
        this.paymentTerm = '';
      } else {
        this.subsidy.selectedIndex = index;
        this.paymentTerm = (this.subsidy.rows[index] || {}).term_label || '';
      }
    },
    async loadSubsidy() {
      const uid = this.order?.order?.uid || this.order?.order?.meta?.uid;
      if (!uid) return;
      this.subsidy.loading = true;
      this.subsidy.error = '';
      try {
        const response = await this.fetchSubsidyTable(uid);
        if (response?.success) {
          this.subsidy.rows = response.rows || [];
          this.subsidy.basePrice = response.base_price ?? null;
          this.subsidy.basePriceNds = response.base_price_nds ?? null;
          this.subsidy.loaded = true;
          if (this.subsidyPanelOpen) this.selectFirstSubsidy();
        } else {
          const message = response?.message || 'Субсидирование недоступно';
          this.subsidy.error = message === 'Субсидирование недоступно для этого клиента' ? '' : message;
        }
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.subsidy.error = (typeof detail === 'string' ? detail : detail?.detail || detail?.message) || 'Не удалось загрузить субсидии';
      } finally {
        this.subsidy.loading = false;
      }
    },
    subsidyOptionLabel(row) {
      const { term, percentText, priceText } = this.subsidyRowParts(row);
      return `${term} · ${percentText} · ${priceText}`;
    },
    subsidyRowParts(row) {
      const base = this.subsidyDisplayBase;
      const price = this.subsidyRowPrice(row);
      return {
        term: this.prettyTerm(row.term_label),
        percentText: `${row.reduction_percent.toLocaleString('ru-RU')}%`,
        priceText: price != null ? this.formatMoney(price) : '—',
        savingsText: price != null && base != null ? this.formatMoney(base - price) : '—',
      };
    },
    subsidyRowPrice(row) {
      const reported = this.withNds ? row.price_with_nds : row.price_without_nds;
      return reported ?? this.applyDiscount(this.subsidyDisplayBase, row.reduction_percent);
    },
    selectFirstSubsidy() {
      if (this.selectedSubsidyRow) return;
      const first = this.subsidyChoices[0];
      if (first) this.chooseSubsidy(first.index);
    },
    formatMoney(value) {
      if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
      return value.toLocaleString('ru-RU', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    },
    requestApproval() {
      const fields = {};
      if (this.priceChanged && this.parsedPrice != null) {
        if (this.withNds) fields.new_cost_with_nds = Math.round(this.parsedPrice);
        else fields.new_cost_without_nds = Math.round(this.parsedPrice);
      }
      const term = this.selectedSubsidyRow ? this.selectedSubsidyRow.term_label : this.paymentTerm;
      if (term) fields.new_pay = toApprovePaymentTerm(term);
      if (this.dateChanged) {
        const iso = this.naiveToIso(this.adjustedDate);
        if (iso) fields.new_date = iso;
      }
      if (!Object.keys(fields).length) {
        this.approvalHint = 'Нечего согласовывать — измените цену, срок или дату';
        return;
      }
      this.approvalHint = '';
      this.$emit('requestApproval', { withNds: this.withNds, fields });
    },
    adjustedPlan(nds, price) {
      const parts = [];
      if (price != null) parts.push(`цена ${this.formatMoney(price)}`);
      if (this.dateChanged) parts.push(`дата ${this.adjustedDate.replace('T', ' ')}`);
      if (this.paymentTerm) parts.push(`срок ${this.prettyTerm(this.paymentTerm)}`);
      return {
        operation: 'closeOrder',
        mode: 'close',
        payload: this.adjustedPayload(nds, price),
        price: price ?? this.effectiveValue,
        hint: `Корректировка админа: ${parts.join(' · ')}`,
      };
    },
    adjustedPayload(nds, price) {
      const adjusted = {};
      if (price != null) {
        if (nds) adjusted.new_cost_with_nds = Math.round(price);
        else adjusted.new_cost_without_nds = Math.round(price);
      }
      if (this.adjustedDate) adjusted.new_date = this.naiveToIso(this.adjustedDate);
      if (this.paymentTerm) adjusted.new_pay = this.paymentTerm;
      return { with_nds: nds, use_adjusted_data: true, adjusted };
    },
    naiveFromSource(value) {
      if (!value) return '';
      const s = String(value).trim();
      if (/[Zz]$|[+-]\d{2}:\d{2}$/.test(s)) {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
      }
      const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
      return m ? `${m[1]}T${m[2]}` : '';
    },
    shiftNaiveHours(naiveInput, hours) {
      const m = naiveInput.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
      if (!m) return naiveInput;
      const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])) + hours * 3600000;
      const d = new Date(t);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    },
    naiveToIso(naiveInput) {
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(naiveInput) ? `${naiveInput}:00` : null;
    },
    prettyTerm(term) {
      return prettyPaymentTerm(term);
    },
    applyDiscount(value, percent) {
      if (value == null || !Number.isFinite(Number(value))) return null;
      return Math.round(Number(value) * (1 - percent / 100) * 10) / 10;
    },
    money(value) {
      if (typeof value !== 'number') return '—';
      return value.toLocaleString('ru-RU');
    },
    applyClose() {
      if (!this.plan.operation || this.plan.error) return;
      if (this.plan.mode === 'approval') {
        this.requestApproval();
        return;
      }
      this.$emit('close', { operation: this.plan.operation, payload: { ...this.plan.payload } });
    },
  },
};
</script>

<style scoped>
.close-panel {
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #d7dce2;
  border-radius: 8px;
  background: #fbfcfe;
  width: 100%;
  box-sizing: border-box;
}
.advance-term-select { width: 100%; padding: 7px; border: 1px solid #d3d6db; border-radius: 6px; background: white; }

.close-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.close-panel-title {
  font-weight: 700;
  font-size: 13px;
  color: #333;
}

.close-grid {
  display: grid;
  grid-template-columns: minmax(92px, max-content) minmax(150px, 1.2fr) minmax(140px, 1fr) minmax(170px, 1.2fr);
  gap: 8px;
  align-items: start;
}


@media (max-width: 720px) {
  .close-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.close-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.close-label {
  font-size: 11px;
  color: #7b8087;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.close-sub {
  font-weight: 400;
}

.close-block input,
.close-block select {
  padding: 5px 8px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  font-size: 13px;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  height: 30px;
  background: #fff;
  color: #333;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.close-block input:hover:not(:disabled),
.close-block select:hover:not(:disabled) {
  border-color: #9ec5fe;
  background: #f8fbff;
}

.close-block input:focus:not(:disabled),
.close-block select:focus:not(:disabled) {
  border-color: #084298;
  box-shadow: 0 0 0 2px rgba(8, 66, 152, 0.12);
  outline: none;
}

.close-block input:disabled,
.close-block select:disabled {
  background: #eef0f3;
  color: #888;
  cursor: not-allowed;
}

.nds-toggle {
  display: flex;
  flex-direction: column;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
}

.nds-btn {
  padding: 0 8px;
  border: 0;
  background: #fff;
  color: #555;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  height: 30px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nds-btn:hover:not(:disabled):not(.active) {
  background: #eef3fb;
  color: #084298;
}

.nds-btn.active {
  background: #084298;
  color: #fff;
}

.nds-btn.active:hover:not(:disabled) {
  background: #06316e;
}

.nds-btn + .nds-btn {
  border-top: 1px solid #d7dce2;
}

.nds-btn.active {
  background: #084298;
  color: #fff;
}

.nds-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quick-row {
  display: flex;
  gap: 4px;
}

.quick-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  background: #fff;
  color: #444;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  height: 30px;
  box-sizing: border-box;
  padding: 0;
}

.close-panel.close-panel,
.close-panel.close-panel * {
  user-select: none;
  -webkit-user-select: none;
}

.close-panel .close-hint.close-hint {
  user-select: text;
  -webkit-user-select: text;
}

.quick-btn:hover:not(:disabled) {
  background: #eef3fb;
  border-color: #9ec5fe;
}

.quick-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.quick-reset:hover:not(:disabled) {
  background: #fdecec;
  border-color: #e3a8a8;
  color: #b83235;
}

.quick-reset {
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.term-dd {
  position: relative;
}

.term-select {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  height: 30px;
  width: 100%;
  padding: 0 8px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 13px;
  font-weight: 400;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.term-select:hover:not(:disabled) {
  border-color: #9ec5fe;
  background: #f8fbff;
}

.term-select.open {
  border-color: #084298;
  box-shadow: 0 0 0 2px rgba(8, 66, 152, 0.12);
}

.term-select:disabled {
  background: #eef0f3;
  color: #888;
  cursor: not-allowed;
}

.term-select-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.term-caret {
  font-size: 11px;
  flex: none;
  color: #7b8087;
}

.term-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 60;
  width: max-content;
  min-width: max(100%, 230px);
  overflow: visible;
  display: flex;
  flex-direction: column;
  padding: 4px;
  background: #fff;
  border: 1px solid #d7dce2;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(30, 40, 60, 0.16);
}

.term-option {
  text-align: left;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #333;
  font-size: 13px;
  font-weight: 400;
  font-family: inherit;
  padding: 7px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.1s ease, color 0.1s ease;
}

.term-option:hover,
.term-option:focus-visible {
  background: #eef3fb;
  color: #084298;
  outline: none;
}

.term-option.selected {
  background: #084298;
  color: #fff;
}

.subsidy-close {
  margin: 12px 0;
  border: 1px solid #c4b5fd;
  border-radius: 10px;
  background: #faf5ff;
  overflow: hidden;
}

.subsidy-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 36px;
  width: 100%;
  padding: 7px 10px;
  border: 0;
  border-radius: 9px;
  background: #6d28d9;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.subsidy-toggle:hover:not(:disabled) {
  background: #5b21b6;
}

.subsidy-toggle.open {
  border-radius: 9px 9px 0 0;
  background: #5b21b6;
}

.subsidy-toggle-title {
  font-weight: 700;
}

.subsidy-toggle-hint {
  color: #ede9fe;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subsidy-caret {
  margin-left: auto;
  font-size: 11px;
  flex: none;
}

.subsidy-content {
  max-height: 320px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  padding: 6px 10px 10px;
  background: #faf5ff;
}

.subsidy-option {
  text-align: left;
  border: 1px solid #e9d5ff;
  border-radius: 6px;
  background: transparent;
  color: #581c87;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  padding: 7px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.1s ease, color 0.1s ease;
}

.subsidy-table-head {
  display: grid;
  grid-template-columns: minmax(92px, 1fr) 56px 92px 92px;
  column-gap: 8px;
  padding: 4px 10px 2px;
  color: #7e22ce;
  font-size: 11px;
  font-weight: 700;
}

.subsidy-table-head span:not(:first-child) {
  text-align: right;
}

.subsidy-row {
  display: grid;
  grid-template-columns: minmax(92px, 1fr) 56px 92px 92px;
  column-gap: 8px;
  align-items: center;
}

.cell-term {
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-percent,
.cell-price,
.cell-savings {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.subsidy-option:hover,
.subsidy-option:focus-visible {
  background: #f3e8ff;
  color: #6b21a8;
  outline: none;
}

.subsidy-option.selected {
  background: #6d28d9;
  color: #fff;
}

.subsidy-option.selected:hover {
  background: #5b21b6;
  color: #fff;
}

.subsidy-toggle:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.subsidy-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px solid #ddd6fe;
  border-radius: 7px;
  background: #fff;
  color: #6b21a8;
  font-size: 11px;
}

.subsidy-summary strong {
  color: #581c87;
  font-size: 12px;
  white-space: nowrap;
}

@media (max-width: 560px) {
  .subsidy-table-head,
  .subsidy-row {
    grid-template-columns: minmax(75px, 1fr) 48px 76px 76px;
    column-gap: 5px;
    font-size: 12px;
  }

  .subsidy-summary {
    align-items: flex-start;
    flex-direction: column;
  }
}

.close-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
}

.close-error {
  color: #b83235;
  font-weight: 600;
}

.close-approval-warn {
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 6px 10px;
  border: 1px solid #f0b26a;
  border-radius: 6px;
  background: #fff3e0;
  color: #8a5300;
  font-weight: 500;
}

.close-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.close-main-btn {
  flex: 1 1 200px;
}

.operation-btn {
  padding: 6px 10px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.operation-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.operation-primary {
  background: #dcfce7;
  border-color: #7dd8ab;
  color: #14663c;
}

.operation-primary:hover:not(:disabled) {
  background: #c8f5d9;
}

.operation-advance {
  background: #e8f1ff;
  border-color: #93c5fd;
  color: #1d4ed8;
}

.operation-advance:hover:not(:disabled) {
  background: #dbeafe;
}

.operation-approval {
  background: #fff3e0;
  border-color: #f0b26a;
  color: #8a5300;
}

.operation-approval:hover:not(:disabled) {
  background: #ffe9c9;
}

.operation-subsidy {
  background: #f0e8fe;
  border-color: #c3aefc;
  color: #5b3fb5;
}

.operation-subsidy:hover:not(:disabled) {
  background: #e9dffd;
}

.operation-secondary {
  background: #fff3e0;
  border-color: #f0b26a;
  color: #8a5300;
}

.operation-secondary:hover:not(:disabled) {
  background: #ffe9c9;
}

.reset-all-btn {
  background: #fdecec;
  border-color: #e3a8a8;
  color: #b83235;
  font-size: 16px;
  line-height: 1;
  padding: 6px 12px;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.reset-all-btn:hover:not(:disabled) {
  background: #f9dcdc;
  border-color: #d98a8a;
  color: #8f1f22;
}
</style>
